import React from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import startCase from "lodash/startCase";

import ClientSearch from "../../../components/ClientSearch";
import MuiPhoneNumber from "../../../components/PhoneNumber";
import { TIpdCase } from "../../../interfaces/IpdInterface";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { clientSearchSecondaryLabel } from "../../../models/Client";
import ReferrerCreateSelect from "../../Referrers/ReferrerCreateSelect";
import { IPDFormState } from "./IpdAdmissionCreateEdit";
import { isValidPhone } from "../../../helpers/phoneNumber";
import useGetNextIpdNumber from "../../../hooks/useGetNextIpdNumber";
import { t, tl } from "../../../components/translate";
import useIsSSFEnabled from "../../../hooks/useIsSSFEnabled";
import { useAppDispatch } from "../../../store/hooks";
import { getActiveClaimCode } from "../../../api/ssf";
import { notificationAdd } from "../../../actions/notification";
import { commonErrorMessage } from "../../../helpers/messages";
import { Ward } from "../../../interfaces/WardInterface";
import { Room } from "../../../interfaces/RoomInterface";
import { Bed } from "../../../interfaces/BedInterface";
import { IpdCases } from "../../../constants/ipd";
import { ExtendedBed } from "../../../hooks/useWardTypeData";

// @ts-expect-error - css
import styles from "../styles.module.css";

export default function IpdAdmissionForm({
  setIpdFormData,
  ipdFormData,
  serviceProviders,
  mode,
  openedFromBed = false,
  wardOptions = [],
  roomOptions = [],
  bedOptions = []
}: {
  setIpdFormData: (data: IPDFormState) => void;
  ipdFormData: IPDFormState;
  serviceProviders: ServiceProvider[];
  mode: "edit" | "create";
  openedFromBed?: boolean;
  wardOptions: Array<Ward>;
  roomOptions: Array<Room>;
  bedOptions: Array<ExtendedBed>;
}): React.ReactNode {
  const dispatch = useAppDispatch();

  // custom hooks
  const { isSsfEnabled } = useIsSSFEnabled();

  const ipdNumber = useGetNextIpdNumber(mode === "create");

  // local states
  const [isSsfIpd, setIsSsfIpd] = React.useState(false);
  const [validPhone, setValidPhone] = React.useState(false);
  const [selectedBed, setSelectedBed] = React.useState<Bed | null>(null);
  const [selectedWard, setSelectedWard] = React.useState<Ward | null>(null);
  const [selectedRoom, setSelectedRoom] = React.useState<Room | null>(null);

  // effects
  React.useEffect(() => {
    // for ward
    if (ipdFormData?.wardId) {
      const ipdWard = wardOptions.find((ward) => ward.id === ipdFormData.wardId);
      setSelectedWard(ipdWard || null);
    } else {
      setSelectedWard(null);
    }

    // for room
    if (ipdFormData?.roomId) {
      const ipdRoom = roomOptions.find((room) => room.id === ipdFormData.roomId);
      setSelectedRoom(ipdRoom || null);
    } else {
      setSelectedRoom(null);
    }

    // for bed
    if (ipdFormData?.bedId) {
      setSelectedBed(bedOptions.find((bed) => bed.id === ipdFormData.bedId) || null);
    } else {
      setSelectedBed(null);
    }
  }, [
    bedOptions,
    ipdFormData.bedId,
    ipdFormData.roomId,
    ipdFormData.wardId,
    roomOptions,
    wardOptions
  ]);

  React.useEffect(() => {
    (async () => {
      if (ipdFormData?.client?.id && isSsfIpd) {
        try {
          const code = await getActiveClaimCode(ipdFormData.client.id);
          setIpdFormData({ ...ipdFormData, claimCode: code.claimCode });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: e.msg || commonErrorMessage,
              autoTimeout: false
            })
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipdFormData, ipdFormData?.client?.id, isSsfIpd]);

  return (
    <Box className={styles.root}>
      <Grid container rowSpacing={3}>
        {/* Basic Information */}
        <Grid container size={12} spacing={2}>
          {isSsfEnabled && (
            <Grid size={12} display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isSsfIpd}
                    onChange={() => {
                      setIsSsfIpd(!isSsfIpd);
                      setIpdFormData({ ...ipdFormData, claimCode: "", client: null });
                    }}
                  />
                }
                label="Is SSF"
              />
              {isSsfIpd && ipdFormData?.client?.id && (
                <Typography>
                  {`Claim code: ${
                    ipdFormData.claimCode || "Not found, please create bill for ipd first."
                  }`}
                </Typography>
              )}
            </Grid>
          )}

          <Grid size={12}>
            <Typography>
              IPD Number:{" "}
              <strong>
                {mode === "create" ? ipdNumber?.nextNumber || "" : ipdFormData.ipdNumber}
              </strong>
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <ClientSearch
              data-testmation="clientSearch"
              autoFocus={!ipdFormData.client}
              client={ipdFormData.client}
              setClient={(v) => setIpdFormData({ ...ipdFormData, client: v })}
              variant="outlined"
              isDisabled={mode === "edit"}
              margin="dense"
              label={t("selectClient")}
              placeholder={t("selectClient")}
              secondaryText={[
                { type: clientSearchSecondaryLabel.CUSTOMER_NUMBER },
                {
                  type: clientSearchSecondaryLabel.EXTERNAL_IDENTIFIER
                }
              ]}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              margin="dense"
              label={tl("file/referenceNumber")}
              variant="outlined"
              placeholder={t("file/referenceNumber")}
              value={ipdFormData.referenceNumber}
              onChange={(e) => setIpdFormData({ ...ipdFormData, referenceNumber: e.target.value })}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </Grid>
        </Grid>

        {/* Admission Location */}
        <Grid container size={12} spacing={2}>
          <Grid size={12}>
            <Typography sx={{ fontWeight: 600 }}>Admission Location</Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Autocomplete
              disabled={openedFromBed}
              value={selectedWard}
              options={wardOptions}
              getOptionLabel={(item) => item.shortCode}
              data-testmation="wardId"
              id="wardId"
              onChange={(_, v) => {
                if (!v || typeof v === "string") {
                  setIpdFormData({
                    ...ipdFormData,
                    wardId: null,
                    roomId: null,
                    bedId: null
                  });
                  return;
                }
                setIpdFormData({
                  ...ipdFormData,
                  wardId: v.id,
                  roomId: null,
                  bedId: null
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={tl("wardName")}
                  placeholder={t("wardName")}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Autocomplete
              disabled={openedFromBed || !ipdFormData.wardId}
              value={selectedRoom}
              options={roomOptions.filter(
                (room) => Number(room.wardId) === Number(ipdFormData.wardId)
              )}
              getOptionLabel={(item) => item.name}
              data-testmation="roomId"
              id="roomId"
              onChange={(_, v) => {
                if (!v || typeof v === "string") {
                  setIpdFormData({
                    ...ipdFormData,
                    roomId: null,
                    bedId: null
                  });
                  return;
                }
                setIpdFormData({
                  ...ipdFormData,
                  roomId: v.id,
                  bedId: null
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={tl("roomName")}
                  placeholder={t("roomName")}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Autocomplete
              disabled={openedFromBed || !ipdFormData.roomId}
              value={selectedBed}
              options={bedOptions.filter(
                (bed) => Number(bed.roomId) === Number(ipdFormData.roomId) && !bed.isOccupied
              )}
              getOptionLabel={(item) => item.number}
              data-testmation="bedId"
              id="bedId"
              onChange={(_, v) => {
                if (!v || typeof v === "string") {
                  setIpdFormData({
                    ...ipdFormData,
                    bedId: null
                  });
                  return;
                }
                setIpdFormData({
                  ...ipdFormData,
                  bedId: v.id
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={tl("bedNumber")}
                  placeholder={t("bedNumber")}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Emergency Contact */}
        <Grid container size={12} spacing={2} sx={{ display: "flex", alignItems: "center" }}>
          <Grid size={12}>
            <Typography sx={{ fontWeight: 600 }}>{tl("emergencyContactDetails")}</Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              data-testmation="name"
              id="secondaryContactName"
              label={tl("guardianName")}
              placeholder={t("guardianName")}
              variant="outlined"
              size="small"
              required
              margin="dense"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              value={ipdFormData.emergencyContact.name}
              onChange={(e) =>
                setIpdFormData({
                  ...ipdFormData,
                  emergencyContact: {
                    ...ipdFormData.emergencyContact,
                    name: e.target.value
                  }
                })
              }
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <MuiPhoneNumber
              fullWidth
              onlyCountries={["np"]}
              size="small"
              required
              value={ipdFormData.emergencyContact.phone}
              defaultCountry="np"
              onChange={(value: string) => {
                if (value && isValidPhone(value)) {
                  setValidPhone(false);
                  setIpdFormData({
                    ...ipdFormData,
                    emergencyContact: {
                      ...ipdFormData.emergencyContact,
                      phone: value
                    }
                  });
                } else {
                  setValidPhone(true);
                  setIpdFormData({
                    ...ipdFormData,
                    emergencyContact: {
                      ...ipdFormData.emergencyContact,
                      phone: ""
                    }
                  });
                }
              }}
              data-testmation="phone"
              label={tl("phoneNumber")}
              variant="outlined"
              error={validPhone}
              helperText={validPhone ? "Please, Enter the valid phone No !" : ""}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              data-testmation="relation"
              id="relation"
              label={tl("relation")}
              placeholder={t("relation")}
              variant="outlined"
              size="small"
              required
              margin="dense"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              value={ipdFormData.emergencyContact.relation}
              onChange={(e) =>
                setIpdFormData({
                  ...ipdFormData,
                  emergencyContact: {
                    ...ipdFormData.emergencyContact,
                    relation: e.target.value
                  }
                })
              }
            />
          </Grid>
        </Grid>

        {/* Clinical Information */}
        <Grid container size={12} spacing={2}>
          <Grid size={12}>
            <Typography sx={{ fontWeight: 600 }}>Clinical Information</Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: mode === "edit" ? 4 : 6 }}>
            <TextField
              fullWidth
              id="ipd Case"
              data-testmation="case"
              select
              label="Admission IPD Case"
              value={ipdFormData.admissionCase}
              onChange={(e) =>
                setIpdFormData({ ...ipdFormData, admissionCase: e.target.value as TIpdCase })
              }
              placeholder="Select Case"
              variant="outlined"
              size="small"
              required
              margin="dense"
              slotProps={{
                inputLabel: { shrink: true },
                select: { MenuProps: { className: styles.menu } }
              }}
            >
              {Object.values(IpdCases).map((option) => (
                <MenuItem key={option} value={option} id={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {mode === "edit" && (
            <Grid size={{ xs: 12, sm: mode === "edit" ? 4 : 6 }}>
              <TextField
                fullWidth
                id="ipd Case"
                data-testmation="case"
                select
                label="Current IPD Case"
                value={ipdFormData.currentCase}
                onChange={(e) =>
                  setIpdFormData({ ...ipdFormData, currentCase: e.target.value as TIpdCase })
                }
                placeholder="Select Case"
                variant="outlined"
                size="small"
                required
                margin="dense"
                slotProps={{
                  inputLabel: { shrink: true },
                  select: { MenuProps: { className: styles.menu } }
                }}
              >
                {Object.values(IpdCases).map((option) => (
                  <MenuItem key={option} value={option} id={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: mode === "edit" ? 4 : 6 }}>
            <TextField
              fullWidth
              data-testmation="expectedLengthofStay"
              id="ExpectedLengthofStay"
              label={tl("expectedLengthOfStay")}
              placeholder={t("enterDays")}
              variant="outlined"
              size="small"
              margin="dense"
              slotProps={{ inputLabel: { shrink: true } }}
              value={ipdFormData.expectedLengthOfStay}
              onChange={(e) =>
                setIpdFormData({ ...ipdFormData, expectedLengthOfStay: e.target.value })
              }
            />
          </Grid>

          {/* emergency */}
          {ipdFormData.ipdCase === IpdCases.EMERGENCY && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Triage Priority"
                value={ipdFormData.triagePriority ?? ""}
                onChange={(e) => setIpdFormData({ ...ipdFormData, triagePriority: e.target.value })}
                variant="outlined"
                margin="dense"
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="critical">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#e03030"
                      }}
                    />
                    Critical
                  </Box>
                </MenuItem>
                <MenuItem value="urgent">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#e8a020"
                      }}
                    />
                    Urgent
                  </Box>
                </MenuItem>
                <MenuItem value="normal">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#1a7a4a"
                      }}
                    />
                    Normal
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              data-testmation="provisionalDiagonosis"
              sx={{ width: "100%" }}
              id="Provisional Diagnosis"
              label={tl("provisionalDiagnosis")}
              variant="outlined"
              placeholder={t("provisionalDiagnosis")}
              multiline
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) =>
                setIpdFormData({ ...ipdFormData, provisionalDiagnosis: e.target.value })
              }
              value={ipdFormData.provisionalDiagnosis}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              data-testmation="conditionAtAdmission"
              sx={{ width: "100%" }}
              id="Admission Condition"
              label={tl("conditionatAdmission")}
              variant="outlined"
              placeholder={t("conditionatAdmission")}
              multiline
              minRows={2}
              value={ipdFormData.admissionCondition}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) =>
                setIpdFormData({ ...ipdFormData, admissionCondition: e.target.value })
              }
            />
          </Grid>

          <Grid size={12}>
            <TextField
              data-testmation="hopi"
              sx={{ width: "100%" }}
              id="History of Past/Present Illness"
              label="HOPI"
              variant="outlined"
              placeholder="History of Past/Present Illness"
              multiline
              minRows={2}
              value={ipdFormData.hopi}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setIpdFormData({ ...ipdFormData, hopi: e.target.value })}
            />
          </Grid>
        </Grid>

        {/* Personnel Information */}
        <Grid container size={12} spacing={2}>
          <Grid size={12}>
            <Typography sx={{ fontWeight: 600 }}>Personnel Information</Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              data-testmation="labTestSelectSP"
              options={serviceProviders}
              getOptionLabel={(option) => startCase(`${option.firstName} ${option.lastName}`)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  label={tl("selectDoc")}
                  placeholder={t("selectDoc")}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {startCase(`${option.firstName} ${option.lastName}`)}
                </li>
              )}
              value={
                serviceProviders.find((doctor) => doctor.id === ipdFormData.doctorInChargeId) ??
                null
              }
              onChange={(e, value) => {
                setIpdFormData({
                  ...ipdFormData,
                  doctorInChargeId: value?.id || null
                });
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              data-testmation="ipdAuthorized"
              options={serviceProviders}
              getOptionLabel={(option) => startCase(`${option.firstName} ${option.lastName}`)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {startCase(`${option.firstName} ${option.lastName}`)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  required
                  margin="dense"
                  variant="outlined"
                  label={tl("selectAuthorisedPersonal")}
                  placeholder={t("selectAuthorisedPersonal")}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              value={
                serviceProviders.find(
                  (doctor) => doctor.id === ipdFormData.authorizedPersonnelId
                ) ?? null
              }
              onChange={(e, value) => {
                setIpdFormData({
                  ...ipdFormData,
                  authorizedPersonnelId: value?.id || null
                });
              }}
            />
          </Grid>

          {mode === "create" && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReferrerCreateSelect
                width="100%"
                textFieldWidth="100%"
                onReferrerChange={(value) => {
                  if (value) {
                    setIpdFormData({ ...ipdFormData, referredBy: value?.referrer });
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
